import React from 'react';
import { X, Bell, BellOff, Volume2, VolumeX, Smartphone, TrendingUp, DollarSign, ArrowDownCircle, Settings } from 'lucide-react';
import { COLORS } from '../../utils/constants';
import { useNotification } from '../../context/NotificationContext';

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange, disabled = false }) => (
  <button
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    style={{
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      backgroundColor: enabled ? COLORS.primary : COLORS.border,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      position: 'relative',
      transition: 'background-color 0.2s',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: '2px',
        left: enabled ? '22px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }}
    />
  </button>
);

// Setting Row Component
const SettingRow = ({ icon: Icon, title, description, enabled, onChange, disabled = false }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: `1px solid ${COLORS.border}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: `${COLORS.primary}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={18} color={COLORS.primary} />
      </div>
      <div>
        <div style={{ color: COLORS.text, fontWeight: '500', fontSize: '14px' }}>{title}</div>
        {description && (
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginTop: '2px' }}>{description}</div>
        )}
      </div>
    </div>
    <ToggleSwitch enabled={enabled} onChange={onChange} disabled={disabled} />
  </div>
);

const NotificationSettingsModal = ({ onClose }) => {
  const { settings, updateSetting, toggleNotifications, requestPushPermission, pushPermission } = useNotification();

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '480px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Settings size={24} color={COLORS.primary} />
            <h2 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', margin: 0 }}>
              Настройки уведомлений
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.textSecondary,
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {/* Master Switch */}
          <div style={{
            padding: '16px',
            backgroundColor: settings.enabled ? `${COLORS.success}10` : `${COLORS.danger}10`,
            borderRadius: '12px',
            marginBottom: '20px',
            border: `1px solid ${settings.enabled ? COLORS.success : COLORS.danger}40`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {settings.enabled ? (
                  <Bell size={24} color={COLORS.success} />
                ) : (
                  <BellOff size={24} color={COLORS.danger} />
                )}
                <div>
                  <div style={{ color: COLORS.text, fontWeight: '600', fontSize: '16px' }}>
                    {settings.enabled ? 'Уведомления включены' : 'Уведомления отключены'}
                  </div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                    Главный переключатель всех уведомлений
                  </div>
                </div>
              </div>
              <ToggleSwitch enabled={settings.enabled} onChange={toggleNotifications} />
            </div>
          </div>

          {/* Display Settings */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase' }}>
              Отображение
            </div>
            <SettingRow
              icon={Bell}
              title="Toast-уведомления"
              description="Всплывающие уведомления на экране"
              enabled={settings.toastEnabled}
              onChange={(v) => updateSetting('toastEnabled', v)}
              disabled={!settings.enabled}
            />
            <SettingRow
              icon={settings.soundEnabled ? Volume2 : VolumeX}
              title="Звук уведомлений"
              description="Звуковой сигнал при новом уведомлении"
              enabled={settings.soundEnabled}
              onChange={(v) => updateSetting('soundEnabled', v)}
              disabled={!settings.enabled}
            />
            <SettingRow
              icon={Smartphone}
              title="Push-уведомления"
              description={pushPermission === 'denied' 
                ? 'Заблокировано в настройках браузера' 
                : 'Уведомления когда вкладка не активна'}
              enabled={settings.pushEnabled}
              onChange={async (v) => {
                if (v && pushPermission !== 'granted') {
                  await requestPushPermission();
                } else {
                  updateSetting('pushEnabled', v);
                }
              }}
              disabled={!settings.enabled || pushPermission === 'denied'}
            />
          </div>

          {/* Notification Types */}
          <div>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase' }}>
              Типы уведомлений
            </div>
            <SettingRow
              icon={TrendingUp}
              title="Торговые операции"
              description="TP/SL, закрытие позиций, лимитные ордера"
              enabled={settings.tradeNotifications}
              onChange={(v) => updateSetting('tradeNotifications', v)}
              disabled={!settings.enabled}
            />
            <SettingRow
              icon={DollarSign}
              title="Депозиты"
              description="Подтверждение и статус депозитов"
              enabled={settings.depositNotifications}
              onChange={(v) => updateSetting('depositNotifications', v)}
              disabled={!settings.enabled}
            />
            <SettingRow
              icon={ArrowDownCircle}
              title="Выводы"
              description="Статус запросов на вывод"
              enabled={settings.withdrawalNotifications}
              onChange={(v) => updateSetting('withdrawalNotifications', v)}
              disabled={!settings.enabled}
            />
            <SettingRow
              icon={Settings}
              title="Системные"
              description="Технические и системные сообщения"
              enabled={settings.systemNotifications}
              onChange={(v) => updateSetting('systemNotifications', v)}
              disabled={!settings.enabled}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              backgroundColor: COLORS.primary,
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
